import { catalogOpsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import { ApiError } from '~/api/errors';
import type {
  BatchSearchImpactSummaryWire,
  CompetitionWire,
  ProductSearchImpactWire,
  SearchImpactReviewItemWire,
} from '~/features/catalog-operations/types/search-impact.api.types';
import type {
  BatchSearchImpactSummary,
  DescriptorCompetition,
  ProductSearchImpact,
  SearchImpactReviewItem,
  SearchImpactSummary,
} from '~/features/catalog-operations/types/search-impact.types';

/** Matrix columns used when the payload carries none (NOT_COMPUTED). */
const DEFAULT_FIELDS: ProductSearchImpact['fields'] = [
  { key: 'title', label: 'Title' },
  { key: 'description', label: 'Description' },
  { key: 'highlights', label: 'Highlights' },
  { key: 'details', label: 'Product details' },
  { key: 'color', label: 'Color' },
  { key: 'type', label: 'Product type' },
];

const EMPTY_SUMMARY: SearchImpactSummary = {
  descriptorsReached: { before: 0, after: 0, total: 0 },
  volumeCovered: { before: 0, after: 0, addressable: 0 },
  attributes: {
    before: 0,
    after: 0,
    gained: 0,
    lost: 0,
    renamed: 0,
    merged: 0,
    narrowed: 0,
    unsupported: 0,
  },
  demandChange: { added: 0, lost: 0, net: 0 },
  needsReview: 0,
};

const COMPETITION: Record<CompetitionWire, DescriptorCompetition> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
};

function toReviewItem(wire: SearchImpactReviewItemWire): SearchImpactReviewItem {
  return {
    id: wire.id,
    itemKey: wire.itemKey,
    kind: wire.kind,
    severity: wire.severity,
    title: wire.title,
    body: wire.body,
    volumeAtRisk: wire.volumeAtRisk,
    dismissed: wire.dismissed,
  };
}

/** Wire → UI. Lists default to empty so NOT_COMPUTED / FAILED render without guards. */
export function toProductSearchImpact(wire: ProductSearchImpactWire): ProductSearchImpact {
  return {
    productId: wire.productId,
    status: wire.status,
    error: wire.error,
    logicVersion: wire.logicVersion ?? '',
    confidence: wire.confidence,
    generatedAt: wire.generatedAt,
    fields: wire.fields?.length ? wire.fields : DEFAULT_FIELDS,
    summary: wire.summary ?? EMPTY_SUMMARY,
    descriptors: (wire.descriptors ?? []).map((d) => ({
      term: d.term,
      monthlyVolume: d.monthlyVolume,
      competition: d.competition ? COMPETITION[d.competition] : null,
      competitionIndex: d.competitionIndex,
      placement: d.placementAfter,
      placementBefore: d.placementBefore,
      snippets: d.snippets,
      reachedBefore: d.reachedBefore,
      reachedAfter: d.reachedAfter,
      withheldReason: d.notPlacedReason,
    })),
    ledger: (wire.ledger ?? []).map((r) => ({
      id: r.id,
      verdict: r.verdict,
      reason: r.reason,
      attribute: r.attribute,
      group: r.group ?? 'Other',
      previous: r.previous,
      value: r.value,
      evidence: r.evidence,
      serves: r.serves.map((s) => ({
        keyword: s.keyword,
        source: s.source,
        monthlyVolume: s.monthlyVolume,
        competitionIndex: s.competitionIndex,
        bidLow: s.bidLow,
        bidHigh: s.bidHigh,
        yoyPct: s.yoyPct,
        peakMonth: s.peakMonth,
        coverage: s.coverage,
        note: s.note,
      })),
      volumeChange: r.volumeChange,
    })),
    review: (wire.review ?? []).map(toReviewItem),
    cleared: (wire.cleared ?? []).map((c) => ({ label: c.label, result: c.result })),
  };
}

/** Loads a product's Search Impact. `enrichmentJobId` pins the producer (results are per batch). */
export async function getProductSearchImpact(
  productId: string,
  enrichmentJobId?: string,
): Promise<ProductSearchImpact> {
  const wire = await catalogOpsClient
    .get(endpoints.catalogOperations.productSearchImpact(productId), {
      searchParams: enrichmentJobId ? { enrichmentJobId } : {},
    })
    .json<ProductSearchImpactWire>();
  return toProductSearchImpact(wire);
}

/** Recomputes one product (synchronous, deterministic) and returns the fresh payload. */
export async function refreshProductSearchImpact(
  productId: string,
  enrichmentJobId?: string,
): Promise<ProductSearchImpact> {
  const wire = await catalogOpsClient
    .post(endpoints.catalogOperations.productSearchImpactRefresh(productId), {
      searchParams: enrichmentJobId ? { enrichmentJobId } : {},
    })
    .json<ProductSearchImpactWire>();
  return toProductSearchImpact(wire);
}

/** Dismisses (or, with `undo`, restores) one review item. Returns the updated item. */
export async function setReviewItemDismissed(
  productId: string,
  itemKey: string,
  dismissed: boolean,
  enrichmentJobId?: string,
): Promise<SearchImpactReviewItem> {
  const url = endpoints.catalogOperations.productSearchImpactDismiss(productId, itemKey);
  const searchParams = enrichmentJobId ? { enrichmentJobId } : {};
  const request = dismissed
    ? catalogOpsClient.post(url, { searchParams, json: {} })
    : catalogOpsClient.delete(url, { searchParams });
  return toReviewItem(await request.json<SearchImpactReviewItemWire>());
}

/** Batch strip figures; null when the batch has no Search Impact rows yet (404). */
export async function getBatchSearchImpactSummary(
  jobId: string,
): Promise<BatchSearchImpactSummary | null> {
  try {
    const wire = await catalogOpsClient
      .get(endpoints.catalogOperations.jobSearchImpactSummary(jobId))
      .json<BatchSearchImpactSummaryWire>();
    return {
      jobId: wire.jobId,
      productCount: wire.productCount,
      flaggedCount: wire.flaggedCount,
      coveredPctBefore: wire.coveredPctBefore,
      coveredPctAfter: wire.coveredPctAfter,
      addressableVolume: wire.addressableVolume,
      generatedAt: wire.generatedAt,
    };
  } catch (err) {
    if (err instanceof ApiError && err.statusCode === 404) return null;
    throw err;
  }
}
